"use client";

import React, { useEffect } from "react";
import { Stat, StatsProps } from "../types";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { useInView } from "react-intersection-observer";
import { backgroundColors, textColors, dividerColors } from "../colors";
import ResponsiveSlider from "@/components/ui/ResponsiveSlider";

const renderSource = (
  source: { url: string; label: string },
  textColor: string,
  className: string,
) => {
  return (
    <p className={`text-base italic mb-0 ${className}`}>
      <span className={textColor}>Source: </span>
      <a
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline cursor-pointer text-blue-600"
      >
        {source.label}
      </a>
    </p>
  );
};

const Source = ({
  stats,
  background = "putty",
  trackEvents = false,
  maxStats = 3,
  header,
  source,
  showSliderOnMobile = false,
}: StatsProps) => {
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      text: "Program Stats",
      htmlId: "stats",
      name: "stats",
      location: "program-detail-page",
      position: "1",
    },
  });

  const { ref: contentRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (trackEvents && inView) {
      elementViewed();
    }
  }, [trackEvents, inView, elementViewed]);

  const backgroundColorClass = backgroundColors[background];
  const dividerColor = dividerColors[background];
  const textColor = textColors[background];

  return (
    <div
      className={`${backgroundColorClass} ${textColor} px-4 flex flex-col gap-10 font-sans`}
    >
      {header && (
        <div className="flex flex-col items-center justify-center w-full text-center">
          <h2 className="text-3xl font-bold md:font-black m-0">{header}</h2>
        </div>
      )}
      {showSliderOnMobile ? (
        <div className="md:hidden">
          <ResponsiveSlider
            desktopChildrenContainerClassName="hidden"
            showInstructions={false}
            navigationPosition="bottom"
          >
            {stats.slice(0, maxStats).map((stat: Stat, index: number) => (
              <div
                key={`${stat.label}-${index}`}
                className="flex flex-col items-center gap-2"
              >
                <span className="text-4xl font-black italic leading-none text-center">
                  {stat.value}
                </span>
                <div className="text-center text-md text-gray-800">
                  <span className="m-0">{stat.label}</span>
                </div>
                {source && renderSource(source, textColor, "mt-6 !mb-8")}
              </div>
            ))}
          </ResponsiveSlider>
        </div>
      ) : null}

      <div
        ref={contentRef}
        className={`${backgroundColorClass} gap-6 flex flex-col md:flex-row lg:justify-around lg:items-start ${showSliderOnMobile ? "hidden md:flex" : ""}`}
      >
        {stats.map((stat: Stat, index: number) => (
          <React.Fragment key={`${stat.label}-${index}`}>
            <div
              className={`flex flex-col ${index >= maxStats ? "md:hidden" : ""}`}
            >
              <div className="flex flex-col items-center gap-2">
                <span className="text-4xl md:text-5xl font-black italic leading-none text-center">
                  {stat.value}
                </span>
                <div className="text-center text-md md:text-base text-gray-800">
                  <span className="m-0">{stat.label}</span>
                </div>
              </div>
            </div>

            {/* Divider between items but not after the last one */}
            {index < stats.length - 1 && (
              <div
                className={`self-center w-full h-px md:min-w-[1px] md:w-[1px] md:h-32 ${dividerColor} my-2 md:my-0`}
              ></div>
            )}
          </React.Fragment>
        ))}
      </div>

      {source &&
        renderSource(
          source,
          textColor,
          showSliderOnMobile ? "hidden md:block" : "",
        )}
    </div>
  );
};

export default Source;
